import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight, Sun, Cpu, Handshake, Briefcase, Sparkles } from 'lucide-react';
import { Button } from './Button';

export const Navbar: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [businessDropdownOpen, setBusinessDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
    setBusinessDropdownOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-surface-border shadow-sm">
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Brand Logo */}
        <Link to="/" className="flex items-center gap-3 group shrink-0">
          <img
            src="/assets/spaq-logo-navy.png"
            alt="Spaq株式会社"
            className="h-9 sm:h-10 w-auto object-contain transition-transform duration-200 group-hover:scale-[1.02]"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
          {/* 事業・サービス Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setBusinessDropdownOpen(true)}
            onMouseLeave={() => setBusinessDropdownOpen(false)}
          >
            <Link
              to="/business"
              className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/business') || isActive('/products') || isActive('/partners')
                  ? 'text-spaq-cyan-dark bg-spaq-cyan-pale/70 font-semibold'
                  : 'text-navy hover:text-spaq-cyan-dark hover:bg-surface-slate'
              }`}
            >
              <span>事業・サービス</span>
              <ChevronDown className="w-4 h-4 opacity-70" />
            </Link>

            {businessDropdownOpen && (
              <div className="absolute left-0 top-full pt-2 w-80 z-50">
                <div className="bg-white rounded-2xl border border-surface-border shadow-soft-lg p-3 space-y-1">
                  <Link
                    to="/business"
                    className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-surface-sky transition-colors"
                  >
                    <div className="w-9 h-9 rounded-lg bg-navy/5 text-navy flex items-center justify-center shrink-0 mt-0.5">
                      <Briefcase className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-navy">事業・サービス一覧</div>
                      <div className="text-xs text-navy-muted">分析・設計から導入・制御・運用まで</div>
                    </div>
                  </Link>

                  <Link
                    to="/products/1minute-solar"
                    className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-surface-sky transition-colors"
                  >
                    <div className="w-9 h-9 rounded-lg bg-spaq-gold-pale text-spaq-gold-dark flex items-center justify-center shrink-0 mt-0.5">
                      <Sun className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-navy flex items-center gap-1.5">
                        1minute solar
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-spaq-gold-pale text-spaq-gold-dark font-semibold">
                          導入前の判断
                        </span>
                      </div>
                      <div className="text-xs text-navy-muted">自家消費型太陽光の概算・相性診断</div>
                    </div>
                  </Link>

                  <Link
                    to="/products/spaq-core"
                    className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-surface-sky transition-colors"
                  >
                    <div className="w-9 h-9 rounded-lg bg-spaq-cyan-pale text-spaq-cyan-dark flex items-center justify-center shrink-0 mt-0.5">
                      <Cpu className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-navy flex items-center gap-1.5">
                        SPAQ CORE
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-spaq-cyan-pale text-spaq-cyan-dark font-semibold">
                          導入後の制御
                        </span>
                      </div>
                      <div className="text-xs text-navy-muted">太陽光・空調・蓄電池の現場協調制御</div>
                    </div>
                  </Link>

                  <Link
                    to="/partners"
                    className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-surface-sky transition-colors"
                  >
                    <div className="w-9 h-9 rounded-lg bg-surface-slate text-navy flex items-center justify-center shrink-0 mt-0.5">
                      <Handshake className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-navy">協業・パートナー</div>
                      <div className="text-xs text-navy-muted">建設・設備・販売・電力事業者様との連携</div>
                    </div>
                  </Link>
                </div>
              </div>
            )}
          </div>

          <Link
            to="/technology"
            className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive('/technology')
                ? 'text-spaq-cyan-dark bg-spaq-cyan-pale/70 font-semibold'
                : 'text-navy hover:text-spaq-cyan-dark hover:bg-surface-slate'
            }`}
          >
            技術と開発
          </Link>

          <Link
            to="/cases"
            className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive('/cases')
                ? 'text-spaq-cyan-dark bg-spaq-cyan-pale/70 font-semibold'
                : 'text-navy hover:text-spaq-cyan-dark hover:bg-surface-slate'
            }`}
          >
            実績・事例
          </Link>

          <Link
            to="/company"
            className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive('/company') || isActive('/news')
                ? 'text-spaq-cyan-dark bg-spaq-cyan-pale/70 font-semibold'
                : 'text-navy hover:text-spaq-cyan-dark hover:bg-surface-slate'
            }`}
          >
            会社情報
          </Link>
        </nav>

        {/* Desktop CTA: 1minute solar を魅力的に強調 */}
        <div className="hidden lg:flex items-center">
          <Link
            to="/products/1minute-solar"
            className="group relative inline-flex items-center gap-2.5 px-4 py-2 rounded-xl bg-gradient-to-r from-navy via-[#14324a] to-navy text-white text-xs font-semibold shadow-soft hover:shadow-cyan-glow border border-spaq-cyan/40 hover:border-spaq-cyan transition-all duration-200 hover:scale-[1.02]"
          >
            <div className="w-6 h-6 rounded-lg bg-spaq-gold/20 border border-spaq-gold/40 flex items-center justify-center text-spaq-gold group-hover:bg-spaq-gold group-hover:text-white transition-colors duration-200">
              <Sun className="w-3.5 h-3.5" />
            </div>
            <div className="flex flex-col text-left">
              <span className="text-[10px] text-spaq-cyan-bright font-mono tracking-wider font-semibold">
                太陽光相性シミュレーター
              </span>
              <span className="text-xs font-bold text-white tracking-wide flex items-center gap-1">
                1minute solar
              </span>
            </div>
            <div className="w-5 h-5 rounded-md bg-white/10 flex items-center justify-center text-spaq-cyan-bright group-hover:translate-x-0.5 group-hover:bg-spaq-cyan group-hover:text-white transition-all duration-200 ml-1">
              <ArrowRight className="w-3 h-3" />
            </div>
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          type="button"
          className="lg:hidden p-2.5 rounded-xl text-navy hover:bg-surface-slate border border-surface-border"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="メニューを開く"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-b border-surface-border px-4 pt-3 pb-6 space-y-3 shadow-soft-lg">
          <div className="grid gap-1">
            <Link
              to="/business"
              className="px-3 py-2.5 rounded-xl text-base font-semibold text-navy hover:bg-surface-sky"
            >
              事業・サービス
            </Link>
            <div className="pl-4 border-l-2 border-spaq-cyan/30 ml-3 space-y-1 my-1">
              <Link
                to="/products/1minute-solar"
                className="block px-3 py-2 rounded-lg text-sm font-medium text-navy-muted hover:text-navy hover:bg-surface-sky"
              >
                • 1minute solar（導入前の判断）
              </Link>
              <Link
                to="/products/spaq-core"
                className="block px-3 py-2 rounded-lg text-sm font-medium text-navy-muted hover:text-navy hover:bg-surface-sky"
              >
                • SPAQ CORE（導入後の現場制御）
              </Link>
              <Link
                to="/partners"
                className="block px-3 py-2 rounded-lg text-sm font-medium text-navy-muted hover:text-navy hover:bg-surface-sky"
              >
                • 協業・パートナー
              </Link>
            </div>
            <Link
              to="/technology"
              className="px-3 py-2.5 rounded-xl text-base font-semibold text-navy hover:bg-surface-sky"
            >
              技術と開発
            </Link>
            <Link
              to="/cases"
              className="px-3 py-2.5 rounded-xl text-base font-semibold text-navy hover:bg-surface-sky"
            >
              実績・事例
            </Link>
            <Link
              to="/company"
              className="px-3 py-2.5 rounded-xl text-base font-semibold text-navy hover:bg-surface-sky"
            >
              会社情報
            </Link>
            <Link
              to="/news"
              className="px-3 py-2.5 rounded-xl text-base font-semibold text-navy hover:bg-surface-sky"
            >
              お知らせ
            </Link>
          </div>

          <div className="pt-3 border-t border-surface-border">
            <Link
              to="/products/1minute-solar"
              className="flex items-center justify-between p-3.5 rounded-xl bg-gradient-to-r from-navy to-[#14324a] text-white border border-spaq-cyan/40 shadow-soft"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-spaq-gold/20 border border-spaq-gold/40 flex items-center justify-center text-spaq-gold shrink-0">
                  <Sun className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <div className="text-[11px] text-spaq-cyan-bright font-semibold">太陽光相性シミュレーター</div>
                  <div className="text-sm font-bold text-white">1minute solar を試す</div>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-spaq-cyan-bright" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
